import React from 'react'
import Select from 'react-select'

import { SelectOption } from '@/components'
import { EnhancedPureComponentWithContext } from '@/components/EnhancedComponents'
import { withFormField } from '@/components/UberForm/FormFieldContext'
import { FormInput } from '@/components/UberForm/types'
import {
	isEditableElement,
	isTextEditingKey,
} from '@/hooks/useKeyboardNavigation'
import {
	ensureWronglyTypedLegacyCodeWorks,
	isMultiOption,
} from '@/lib/components/Select/utils'
import { areArraysEqual, getCustomComponents, nextFrame } from '@/utils'
import { filterPreservingType } from '@/utils/array'

import { getSelectValue } from '../CustomSelectComponents'
import { getStylesConfig } from './styles'
import { SelectProps } from './types'

const SELECT_CLASSNAME_PREFIX = 'react-select'

export class SelectWithoutForm extends EnhancedPureComponentWithContext<
	SelectProps<SelectOption>
> {
	static defaultProps = {
		labelKey: 'label',
		valueKey: 'value',
		clearable: true,
		allowEmpty: true,
	}
	input = React.createRef<Select>()

	async componentDidMount() {
		const { register, refInput } = this.props

		if (refInput && refInput !== this.input) {
			this.input = refInput
		}

		if (register) {
			register(this as FormInput)
		}

		await nextFrame()
	}
	getValueKey = () => this.props.valueKey || 'value'
	getLabelKey = () => this.props.labelKey || 'label'

	componentDidUpdate(prevProps: SelectProps<SelectOption>) {
		const { options, isMulti, allowEmpty, loading, onChange, value } =
			this.props

		if (
			loading ||
			(loading === prevProps.loading &&
				options === prevProps.options &&
				value === prevProps.value &&
				(value || allowEmpty))
		) {
			return
		}
		let updatedValue = value

		if (isMulti) {
			if (value && Array.isArray(value)) {
				updatedValue = this.props.options
					? filterPreservingType(
							value,
							(item) =>
								!!this.props.options?.find(
									(option) => this.getOptionValue(option) === item,
								),
						)
					: []
			}
		} else {
			updatedValue =
				value === null || value === undefined
					? value
					: this.props.options &&
						(this.props.options.some(
							(option) => this.getOptionValue(option) === value,
						)
							? value
							: allowEmpty
								? undefined
								: this.props.options[0] &&
									this.getOptionValue(this.props.options[0]))
		}

		if (updatedValue === value) {
			return
		}

		if (
			isMulti &&
			Array.isArray(updatedValue) &&
			Array.isArray(value) &&
			areArraysEqual(updatedValue, value)
		) {
			return
		}

		if (!updatedValue) {
			return
		}

		onChange && onChange(updatedValue)
	}

	onChange = (
		newValue: readonly SelectOption[] | SelectOption | undefined | null,
	) => {
		const { isMulti, options, allowEmpty, onChange, onCoerseValue, value } =
			this.props

		if (onCoerseValue && options) {
			const oldValue = isMulti
				? value && Array.isArray(value)
					? value.map(
							(val) =>
								options.filter(
									(option) => this.getOptionValue(option) === val,
								)[0],
						)
					: []
				: value
					? options.filter((option) => this.getOptionValue(option) === value)
					: undefined

			newValue = onCoerseValue(oldValue, newValue)
		}

		onChange &&
			onChange(
				isMulti
					? newValue && isMultiOption(newValue)
						? newValue.map((option) => this.getOptionValue(option))
						: []
					: newValue && !isMultiOption(newValue)
						? this.getOptionValue(newValue)
						: allowEmpty
							? undefined
							: options && options[0] && this.getOptionValue(options[0]),
			)
	}

	getOptionValue = (option: SelectOption): string => {
		const selectRawValue = option[this.getValueKey()]

		if (!selectRawValue) {
			return ''
		}

		return ensureWronglyTypedLegacyCodeWorks(selectRawValue)
	}

	getOptionLabel = (opt: SelectOption) => {
		let label = opt[this.getLabelKey()]?.toString() || ''

		if (opt.wasDeleted) {
			label += ' !'
		}

		return label
	}

	getNoOptionsMessage = () =>
		this.props.noResultText
			? this.props.noResultText
			: this.context.t('FILTER_NO_RESULT_TEXT')

	render() {
		const { t } = this.context

		const {
			className,
			clearable,
			components,
			customStyles,
			isDisabled,
			formatOptionLabel,
			id,
			isLoading,
			isMulti,
			isTableCell,
			name,
			onBlur,
			onFocus,
			onInputChange,
			onMenuClose,
			onMenuOpen,
			openMenuOnFocus,
			options,
			placeholder,
			refInput,
			testId,
			value,
		} = this.props

		const selectValue = getSelectValue(
			this.getOptionValue,
			options,
			value,
			isMulti,
		)

		const getPlaceHolder = (): string => {
			if (isDisabled) return ''
			if (placeholder !== undefined) return placeholder
			return isMulti ? t('FILTER_ALL_PLACEHOLDER') : ''
		}

		return (
			<div
				style={{ width: '100%', cursor: isDisabled ? 'not-allowed' : 'auto' }}
				role="listbox"
				onKeyDown={(e) => {
					if (isEditableElement(e.target) && isTextEditingKey(e.key)) {
						e.stopPropagation()
						return
					}
					if (e.key === 'Enter') {
						e.stopPropagation()
						e.preventDefault()
					}
				}}
				data-testid={testId ? `select-container-${testId}` : 'select-container'}
			>
				<Select
					className={className}
					classNamePrefix={SELECT_CLASSNAME_PREFIX}
					closeMenuOnSelect={!isMulti}
					components={getCustomComponents(components)}
					formatOptionLabel={formatOptionLabel}
					getOptionLabel={this.getOptionLabel}
					getOptionValue={this.getOptionValue}
					hideSelectedOptions={false}
					inputId={id}
					isClearable={clearable}
					isDisabled={isDisabled}
					isMulti={isMulti}
					menuPlacement="auto"
					menuPortalTarget={document.body}
					name={name}
					noOptionsMessage={this.getNoOptionsMessage}
					onBlur={onBlur}
					onChange={this.onChange}
					onFocus={onFocus}
					onInputChange={onInputChange}
					onMenuClose={onMenuClose}
					onMenuOpen={onMenuOpen}
					openMenuOnFocus={openMenuOnFocus}
					options={options}
					placeholder={getPlaceHolder()}
					ref={refInput ?? this.input}
					styles={getStylesConfig({ customStyles, isTableCell })}
					value={selectValue}
					isLoading={isLoading}
					data-testid={testId}
				/>
			</div>
		)
	}
}

export default withFormField(SelectWithoutForm)
